import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios, { startSessionHealthCheck, resetSessionAlert } from '../utils/axiosConfig';
import cffaLogo from '../assets/cffa-logo.png';
import './Login.css';

const Login = ({ onLogin }) => {
    const { t, i18n } = useTranslation();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [accountLocked, setAccountLocked] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [lockedUntil, setLockedUntil] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const handleLanguageChange = (lang) => {
        i18n.changeLanguage(lang);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setAccountLocked(false);
        setLoading(true);

        try {
            const response = await axios.post('/api/auth/login', { email, password });
            // Reset the session alert flag and start health check on successful login
            resetSessionAlert();
            startSessionHealthCheck();
            onLogin(response.data.user);
        } catch (err) {
            if (err.response?.status === 429 && err.response?.data?.code === 'ACCOUNT_LOCKED') {
                setAccountLocked(true);
                setLockedUntil(err.response?.data?.lockedUntil);
                setError(t('login.accountLocked'));
            } else if (err.response?.status === 403 && err.response?.data?.code === 'ACCOUNT_DEACTIVATED') {
                setError(t('login.accountDeactivated'));
            } else {
                setError(t('login.invalidCredentials'));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-container">
            <a href="/#/" className="home-btn-top" title={t('login.backToSite')} aria-label={t('login.backToSite')}>
                <img src={cffaLogo} alt="" className="home-btn-logo" />
            </a>

            <div className="lang-switcher-top">
                <button
                    className={`lang-btn ${i18n.language === 'en' ? 'active' : ''}`}
                    onClick={() => handleLanguageChange('en')}
                >
                    English
                </button>
                <span className="lang-separator">|</span>
                <button
                    className={`lang-btn ${i18n.language === 'fr' ? 'active' : ''}`}
                    onClick={() => handleLanguageChange('fr')}
                >
                    Français
                </button>
            </div>

            <div className="login-card">
                <img className="auth-logo" src={cffaLogo} alt="Gestion & Holdings CFFA" />
                <h1>CFFA Group</h1>
                <p className="login-description">{t('nav.login')}</p>

                {error && (
                    <div className={`error-message ${accountLocked ? 'warning' : 'error'}`}>
                        <span className="error-icon">⚠</span> {error}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="email">{t('login.username')}</label>
                        <input
                            type="text"
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={t('login.username')}
                            required
                            disabled={accountLocked || loading}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">{t('login.password')}</label>
                        <div className="password-input-wrapper">
                            <input
                                type={showPassword ? "text" : "password"}
                                id="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder={t('login.password')}
                                required
                                disabled={accountLocked || loading}
                            />
                            <button
                                type="button"
                                className="visibility-toggle"
                                onClick={() => setShowPassword(!showPassword)}
                                disabled={accountLocked || loading}
                                title={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                            >
                                {showPassword ? '👁️' : '🔒'}
                            </button>
                        </div>
                    </div>

                    <button
                        type="submit"
                        className={`submit-button ${loading ? 'loading' : ''}`}
                        disabled={accountLocked || !email || !password || loading}
                    >
                        {loading ? (
                            <>
                                <span className="spinner"></span>
                                {t('login.signingIn')}
                            </>
                        ) : (
                            t('login.signIn')
                        )}
                    </button>
                </form>

                <div className="form-footer">
                    <p>
                        <a href="/#/forgot-password" className="link">{t('login.forgotPassword')}</a>
                        {accountLocked && (
                            <>
                                <br />
                                <a href="/#/forgot-password" className="link warning">{t('login.resetPassword')}</a>
                            </>
                        )}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Login;
